import React, { useRef, useState } from 'react';
import { Button, FileUpload, LinkInput } from '@lemon-io/ui-kit';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import { profileApi } from '@internal/api/profile';
import { markResumeConfirmed } from '@internal/api/profile/application-stage';
import {
  clearEnrichmentJob,
  rememberEnrichmentJob,
} from '@internal/components/app/components/enrichment-watcher';
import { APP_COLORS } from '@internal/components/app/constants';
import ConfirmModal from '@internal/components/common/confirm-modal/confirm-modal';

import {
  isValidLinkedin,
  LINKEDIN_ERROR_MESSAGE,
  normalizeUrl,
} from './linkedin';
import { MAX_CV_SIZE_MB } from './signup.constants';
import { IconTile, ImportRow as ImportRowRoot } from './signup.styled';
import StepDots from './step-dots';

const ImportRow: React.FC<{
  icon: React.ReactNode;
  title: React.ReactNode;
  helper: string;
  control: React.ReactNode;
}> = ({ icon, title, helper, control }) => (
  <ImportRowRoot>
    <IconTile>{icon}</IconTile>
    <Box sx={{ flex: 1, minWidth: 180 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        {title}
      </Stack>
      <Typography variant="body3" sx={{ color: 'text.secondary50' }}>
        {helper}
      </Typography>
    </Box>
    <Box sx={{ width: '100%', flexShrink: 0 }}>{control}</Box>
  </ImportRowRoot>
);

type ImportedValues = {
  linkedin: string;
  github: string;
  cvFile: File | null;
};

const ImportStep: React.FC<{
  apiKey: string;
  qualified?: boolean;
  initialLinkedin?: string;
  initialGithub?: string;
  parsingStarted?: boolean;
  onContinue: () => void;
}> = ({
  apiKey,
  qualified = true,
  initialLinkedin = '',
  initialGithub = '',
  parsingStarted = false,
  onContinue,
}) => {
  const askLinkedin = !initialLinkedin.trim();
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [confirmCvDelete, setConfirmCvDelete] = useState(false);
  const [linkedin, setLinkedin] = useState(initialLinkedin);
  const [linkedinTouched, setLinkedinTouched] = useState(false);
  const [github, setGithub] = useState(initialGithub);
  const lastImported = useRef<ImportedValues | null>(
    initialLinkedin
      ? { linkedin: initialLinkedin, github: initialGithub, cvFile: null }
      : null
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const linkedinError =
    linkedinTouched && linkedin.trim() && !isValidLinkedin(linkedin)
      ? LINKEDIN_ERROR_MESSAGE
      : undefined;

  const { parseCVAsync } = profileApi.parseCV.useMutation();
  const { updateProfileAsync } = profileApi.updateProfile.useMutation();
  const { triggerEnrichmentAsync } = profileApi.triggerEnrichment.useMutation();
  const { markResumeConfirmedAsync } = markResumeConfirmed.useMutation();

  const handleFilePicked = (file: File) => {
    if (
      file.type !== 'application/pdf' ||
      file.size > MAX_CV_SIZE_MB * 1024 * 1024
    ) {
      setError(`The CV should be a PDF up to ${MAX_CV_SIZE_MB} MB.`);
      return;
    }
    setError(null);
    setCvFile(file);
  };

  const finish = async () => {
    const unchanged =
      lastImported.current &&
      lastImported.current.linkedin === linkedin &&
      lastImported.current.github === github &&
      lastImported.current.cvFile === cvFile;
    // The shortcut is only safe once parsing actually got going — if the
    // signup-time kickoff failed, fall through so the LinkedIn gets saved and
    // enrichment triggered here.
    if (unchanged && (!qualified || parsingStarted)) {
      markResumeConfirmedAsync({ apiKey }).catch(() => {});
      onContinue();
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      let attachmentId: string | undefined;
      if (cvFile) {
        const attachment = new FormData();
        attachment.append('attachment', cvFile);
        const parsed = await parseCVAsync({ apiKey, attachment });
        attachmentId = parsed?.id;
      }

      const linkedinUrl = normalizeUrl(
        askLinkedin ? linkedin : initialLinkedin
      );

      await updateProfileAsync({
        apiKey,
        linkedin: linkedinUrl,
        ...(github.trim() && { codesamples_link: normalizeUrl(github) }),
      });

      markResumeConfirmedAsync({ apiKey }).catch(() => {});

      const needsEnrichment =
        qualified && (Boolean(attachmentId) || askLinkedin || !parsingStarted);
      if (needsEnrichment) {
        rememberEnrichmentJob({ apiKey, jobId: null, storedAt: Date.now() });

        const response = await triggerEnrichmentAsync({
          apiKey,
          attachmentId,
          linkedinUrl,
        }).catch(() => null);
        const jobId = response?.profileEnrichmentJob?.id;
        if (jobId) {
          rememberEnrichmentJob({ apiKey, jobId, storedAt: Date.now() });
        } else if (response) {
          clearEnrichmentJob();
        }
      }

      lastImported.current = { linkedin, github, cvFile };
      onContinue();
      setSubmitting(false);
    } catch {
      setError("Couldn't start the import. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <StepDots total={2} current={0} />
      <Typography variant="h4">Let&apos;s build your profile</Typography>
      <Typography variant="body3" sx={{ mt: 1, color: 'text.primary' }}>
        {askLinkedin
          ? "Add your LinkedIn — we'll build the rest from it. CV and GitHub are optional but help."
          : 'We use LinkedIn as a main source. CV and GitHub are optional but help.'}
      </Typography>

      <Stack spacing={2} sx={{ mt: 3 }}>
        {askLinkedin && (
          <ImportRow
            icon={<LinkedInIcon />}
            title={
              <>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  LinkedIn
                </Typography>
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    bgcolor: APP_COLORS.lilacDeep,
                    color: 'contrast.main',
                    fontWeight: 500,
                  }}
                />
              </>
            }
            helper="linkedin.com/in/…"
            control={
              <LinkInput
                label="Link to Linkedin"
                value={linkedin}
                onChange={setLinkedin}
                onBlur={() => setLinkedinTouched(true)}
                error={linkedinError}
                dataTestId="join-linkedin"
                sx={{ width: '100%' }}
              />
            }
          />
        )}

        <ImportRow
          icon={<FileUploadOutlinedIcon />}
          title={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              CV / Résumé
            </Typography>
          }
          helper={`We expect pdf file only, not heavier than ${MAX_CV_SIZE_MB}MB here.`}
          control={
            <FileUpload
              accept="application/pdf"
              file={cvFile ? { name: cvFile.name, size: cvFile.size } : null}
              dataTestId="join-cv-upload"
              onFileSelected={handleFilePicked}
              onDelete={() => setConfirmCvDelete(true)}
            />
          }
        />

        <ImportRow
          icon={<GitHubIcon />}
          title={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              GitHub
            </Typography>
          }
          helper="github.com/…"
          control={
            <LinkInput
              label="GitHub or link to code samples"
              value={github}
              onChange={setGithub}
              dataTestId="join-github"
              sx={{ width: '100%' }}
            />
          }
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      <Stack sx={{ mt: 5 }}>
        <Button
          variant="main"
          color="primary"
          disabled={(askLinkedin && !isValidLinkedin(linkedin)) || submitting}
          icon={
            submitting ? (
              <CircularProgress size={16} sx={{ color: 'inherit' }} />
            ) : undefined
          }
          iconPlacement="right"
          onClick={() => void finish()}
          sx={{
            width: '100%',
            whiteSpace: 'nowrap',
            ...(submitting && {
              '&.Mui-disabled': { color: 'common.white', opacity: 1 },
            }),
          }}
        >
          {submitting ? 'Starting the import' : 'Continue'}
        </Button>
      </Stack>

      <ConfirmModal
        open={confirmCvDelete}
        isLoading={false}
        title="Delete CV?"
        description="Are you sure you want to delete the uploaded CV?"
        onClose={() => setConfirmCvDelete(false)}
        onConfirm={() => {
          setCvFile(null);
          setConfirmCvDelete(false);
        }}
      />
    </>
  );
};

export default ImportStep;
